import { PortalError } from '@app/utils/error/portal-error';
import { Observable } from 'rxjs';

/**
 * Provides `data$: Observable<T>`, `loading$: Observable<boolean>` and
 * `error$: Observable<HttpErrorResponse | Error | null>` states.
 *
 * Supports re-production of cached data through `reload()`, explicitly setting data to cache
 * through `setData()`, clearing the cache with `clear()`, and supplying new `dataProduction`
 * function with `changeIdentity()`.
 *
 * Note: `<T>` can be of any type, except `undefined`. The `data$` never emits `undefined` values.
 *
 */
export interface Cached<T> {
  /**
   * Emits whenever this instance has been cleared after the last subscription was removed.
   */
  readonly cleared$: Observable<void>;

  /**
   * Emits the cached data. It never completes or errors.
   */
  readonly data$: Observable<T>;

  /**
   * Just as `data$`, only this one will propagate an error to subscribers, and
   * that means "the end" for the stream.
   */
  readonly dataWithError$: () => Observable<T>;

  /**
   * Emits the error that might have occurred during loading/processing the cached data.
   *
   * Initial value is `null`.
   */
  readonly error$: Observable<PortalError | null>;

  /**
   * Whether this instance has subscriptions.
   */
  readonly hasSubscriptions: boolean;

  /**
   * Emits whenever the amount of connections changes.
   */
  readonly hasSubscriptions$: Observable<boolean>;

  /**
   * Current id.
   */
  readonly id: string;

  /**
   * Current value or undefined if the instance is empty or the producer errored.
   *
   * @deprecated Do not use this because it will be removed.
   * Use `data$.pipe(first())` if you need the current value of this instance.
   */
  readonly snapshot: T | undefined;

  /**
   * Emits the current state of loading/processing the cached data.
   *
   * Initial value is `true` and also after clearing the cache.
   */
  readonly loading$: Observable<boolean>;

  /**
   * Get an observable that contains the identifiers of the
   * currently remembered values of this instance. Returns
   * null if this instance does not remember anything.
   */
  readonly remembering$: Observable<CachedRememberingEntry[]> | null;

  /**
   * @param id new cached data ID.
   * @param dataProducer new data production function
   * @returns `true` if the ID has changed, meaning the new `dataProducer` was accepted and
   * executed. On `false` nothing happens.
   */
  changeIdentity(id: string, dataProducer?: () => Observable<T>): boolean;

  /**
   * Clear the cache.
   *
   * Technically this means emitting `undefined` in `data$` and that is never emitted.
   * The error is cleared as well and loading is set to `true`.
   *
   * @param clearRememberedValues Clear all remembered values as well, by default true.
   */
  clear(clearRememberedValues?: boolean): void;

  /**
   * Remove the remembered value that matches the given ID.
   * @param id ID of the entry to remove.
   */
  clearRememberedValue(id: string): void;

  /**
   * Will refresh the cached data by re-executing the `dataProducer` function.
   * Also clears any previously emitted error.
   *
   * @param reloadSources Reload all sources as well, true by default.
   * @returns  `Observable<void>` that emits `undefined` and completes as soon as reload event is done.
   */
  reload(reloadSources?: boolean): Observable<void>;

  /**
   * Reload the instance if there are any subscriptions when called. Otherwise,
   * the instance is cleared.
   *
   * @deprecated Use reload() instead (works now the same way)
   * @param reloadSources Reload all sources as well, true by default.
   */
  reloadOrClear(reloadSources?: boolean): void;

  /**
   * Update the cache explicitly. This will also clear any previously emitted error.
   * @param data The data that should be stored in the cache.
   */
  setData(data: T): void;
  /**
   * Explicitly suspending auto-reload.
   * Do this in case where you know that the data-producer function cannot produce at the time.
   * Auto-reload resumes when `changeIdentity` is called (regardless if the identity really changed)
   * or on first subscription.
   */
  suspendAutomaticReload(): void;
}

/**
 * Options for `Cached` instantiation.
 */
export interface CachedOptions<T> {
  /**
   * `dataProducer` is a function that returns an `Observable<T>`. `params` is filled if `parents`
   * is defined.
   */
  dataProducer: (params?: any[]) => Observable<T>;

  /**
   * `id` is an array of `string` values that in some sense identify the cached data.
   * E.g, user's login name, or dynamic parts of URL for requesting the data. The value can later on be
   * changed through `changeIdentity` by providing also new `dataProducer`.
   */
  id: string;

  /**
   * Reloads the data of this instance every x milliseconds after the last time data was received as
   * long as there is an active subscription.
   */
  reloadInterval?: number;

  /**
   * If the `cached` has also cached sources then they can also be reloaded on automatic reload
   * based on `reloadInterval`. Default is `false`.
   */
  reloadSourcesOnReloadInterval?: boolean;

  /**
   * How many previous values should be remembered (fifo).
   */
  rememberCount?: number;

  /**
   * How long (in milliseconds) previous values (depending on id) should be remembered. If undefined, no previous values are remembered.
   */
  rememberTtl?: number;

  /**
   * Keeping cached data as long as there are subscriptions (with time delay) or forever.
   * Default is `RetentionPolicy.Subscriptions`.
   */
  retention?: RetentionPolicy;

  /**
   * `clearDelay` defaults to 10_000ms. Is the time delay for clearing cached data when
   * subscriptions count drops to zero. Is relevant only in combination with `RetentionPolicy.Subscriptions`.
   */
  clearDelay?: number;

  /**
   * An array of cached objects that are grouped by this instance.
   * Data streams are merged together and emitted as an array.
   * The same applies for the errors streams.
   */
  sources?: Source<any>[];

  /**
   * If true then this instance will update its loading state if the sources start loading.
   * Default false.
   */
  watchSourcesLoadingState?: boolean;
}

/**
 * Options for `cachedGroup` instantiation.
 */
export interface CachedGroupOptions<T> {
  /**
   * `id` is an array of `string` values that in some sense identify the cached data. A cached group
   * should not use dynamic values as part of the id because the identity cannot be changed later on.
   */
  id: string;

  /**
   * Reloads the data of this instance every x milliseconds after the last time data was received as
   * long as there is an active subscription.
   */
  reloadInterval?: number;

  /**
   * If the `cached` also has cached sources then they can also be reloaded on automatic reload based on `reloadInterval`.
   * Default is `false`.
   */
  reloadSourcesOnReloadInterval?: boolean;

  /**
   * Keeping cached data as long as there are subscriptions (with time delay) or forever.
   * Default is `RetentionPolicy.Subscriptions`.
   */
  retention?: RetentionPolicy;

  /**
   * `clearDelay` defaults to 10_000ms. Is the time delay for clearing cached data when
   * subscriptions count drops to zero. Is relevant only in combination with `RetentionPolicy.Subscriptions`.
   */
  clearDelay?: number;

  /**
   * If true then this instance will update its loading state if the sources start loading.
   * Default false.
   */
  watchSourcesLoadingState?: boolean;
}

/**
 * For how long should cached data be kept?
 */
export enum RetentionPolicy {
  Forever = 'FOREVER',
  /**
   * As long as there are subscriptions.
   *
   * Reloading data on first subscription and clearing data when subscriptions drop to zero and
   * the `clearDelay` has elapsed.
   */
  Subscriptions = 'SUBSCRIPTIONS',
}

/**
 * Source whose emitted values can be skipped based on a given function that
 * compares values. This is useful when a source is used that constantly
 * refreshes but seldom changes (e.g. enterprise options).
 */
export interface SourceDefinition<T> {
  /**
   * Cached object that is used as the source
   */
  cached: Cached<T>;

  /**
   * Set to true so cached groups that are using this source
   * ignore the loading state of this particular source.
   */
  ignoreLoadingState?: boolean;

  /**
   * Function that is passed the last two values that have been emitted. If it
   * returns true, then the emitted value is skipped.
   *
   * @param a Current value
   * @param b Previous value
   */
  skipWhen?: (a: T, b: T) => boolean;
}

export type Source<T> = Cached<T> | SourceDefinition<T>;

export interface CachedRememberingEntry {
  /**
   * If this remembered entry is currently active.
   */
  active: boolean;

  /**
   * Unique ID for this entry.
   */
  id: string;
}
